import { Handle, Position } from "@xyflow/react";
import {
  AcChargingPole3PhaseOutput,
  BessDeviceOutput,
  DcChargingPoleOutput,
  ForecastedThreePhaseDeviceOutput,
  Measurement,
} from "../../../../../../services/client/types.gen";
import {
  CarOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { find } from "lodash";
import { useMemo } from "react";

function BatteryIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-label="Battery"
    >
      <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" />
    </svg>
  );
}

const DcNode = ({
  data,
  measurement,
}: {
  data: DcChargingPoleOutput;
  measurement: Measurement | undefined;
}) => {
  const isCharging = (measurement?.powerValue ?? 0) > 0;

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center">
        <div
          className={`rounded-full w-12 h-12 flex justify-center items-center ${isCharging ? "bg-green-100" : "bg-gray-100"}`}
        >
          <CarOutlined />
        </div>
        <div className="ml-2">
          <div className="text-lg font-bold">{data.name}</div>
          <div className="text-gray-500">max power: {data.max_power} W</div>
        </div>
      </div>
    </div>
  );
};

const NonDcNode = ({
  data,
  measurement,
}: {
  data: AcChargingPole3PhaseOutput | ForecastedThreePhaseDeviceOutput;
  measurement: Measurement | undefined;
}) => {
  const isCharging = (measurement?.powerValue ?? 0) > 0;
  const isForecasted = data.type === "forecasted_series_3phase";

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center">
        <div
          className={`rounded-full w-12 h-12 flex justify-center items-center ${isCharging ? "bg-green-100" : "bg-gray-100"}`}
        >
          {isForecasted ? <DashboardOutlined /> : <CarOutlined />}
        </div>
        <div className="ml-2">
          <div className="text-lg font-bold">{data.name}</div>
          <div className="text-gray-500">
            line_current: {data.line_current} amp
          </div>
        </div>
      </div>
    </div>
  );
};

const BessNode = ({
  data,
  measurement,
}: {
  data: BessDeviceOutput;
  measurement: Measurement | undefined;
}) => {
  const isActive = (measurement?.powerValue ?? 0) > 0;

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center">
        <div
          className={`rounded-full w-12 h-12 flex justify-center items-center ${isActive ? "bg-green-100" : "bg-gray-100"}`}
        >
          <BatteryIcon />
        </div>
        <div className="ml-2">
          <div className="text-lg font-bold">{data.name}</div>
          <div className="text-gray-500 text-xs">Battery (BESS)</div>
        </div>
      </div>
    </div>
  );
};

function DeviceNode({
  data,
  measurements,
}: {
  data:
    | AcChargingPole3PhaseOutput
    | DcChargingPoleOutput
    | ForecastedThreePhaseDeviceOutput
    | BessDeviceOutput;
  measurements?: Measurement[];
}) {
  const measurement = useMemo(
    () =>
      find(
        measurements,
        (measurement) => measurement.source.asset_id === data.name,
      ),
    [measurements, data.name],
  );

  const isDcChargingPole =
    data.type === "dc_charging_pole" || "max_power" in data;
  const isBess = data.type === "battery_energy_storage_system";

  return (
    <div
      className={`px-4 py-2 shadow-md rounded-md bg-white border-2 border-stone-400`}
    >
      {isBess ? (
        <BessNode data={data as BessDeviceOutput} measurement={measurement} />
      ) : isDcChargingPole ? (
        <DcNode data={data as DcChargingPoleOutput} measurement={measurement} />
      ) : (
        <NonDcNode
          data={
            data as
              | AcChargingPole3PhaseOutput
              | ForecastedThreePhaseDeviceOutput
          }
          measurement={measurement}
        />
      )}

      {measurement && (
        <div className="mt-2 flex items-center space-x-2">
          <ThunderboltOutlined className="h-6 w-6 text-yellow-500" />{" "}
          <div className="text-3xl font-bold text-gray-800">
            {Math.round(measurement.powerValue)}
          </div>
          <span className="text-sm text-gray-500">w</span>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-16 !bg-teal-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-16 !bg-teal-500"
      />
    </div>
  );
}

export default DeviceNode;
